import { ArrowDown, ArrowRight, CalendarDays, ChartNoAxesCombined, CircleCheck, Handshake, Network, Trophy, UserRound, Users } from "lucide-react";
import type { AgencyPolicySection } from "./agency-policy-content";

export const policyChapterArt = {
  "rebate-model": { Icon: Network, label: "提成返点", hint: "看懂代理点位与差额" },
  "commission-plan": { Icon: ChartNoAxesCombined, label: "佣金计划", hint: "按人数条件查看档位" },
  "invitation-rewards": { Icon: Users, label: "人头费奖励", hint: "三个统计与发放周期" },
  "weekly-rewards": { Icon: Trophy, label: "周流水奖励", hint: "流水与新增同时达标" },
  "partner-plan": { Icon: Handshake, label: "伙伴扩展", hint: "区分两类代理推荐奖励" },
};

function AgentNode({ text }: { text: string }) {
  const [label, points] = text.split("：");
  return <div className="policy-agent"><span className="policy-agent__portrait" aria-hidden="true"><UserRound /></span><span>{label}</span><strong>{points}</strong></div>;
}

export function AgencyPolicyVisual({ section }: { section: AgencyPolicySection }) {
  const table = section.blocks.find(block => block.type === "table");
  if (section.id === "rebate-model" && table?.type === "table") return <figure className="policy-visual policy-visual--network" aria-label="代理点位差额示例">
    <figcaption><Network aria-hidden="true" /><span>点位差额示意<small>制度示例</small></span></figcaption>
    <div className="policy-agent-chain">
      {[table.rows[0], table.rows[1], table.rows[3]].map((row, index) => <div className="policy-agent-chain__step" key={row[0]}>
        <AgentNode text={row[0]} />{index < 2 && <ArrowRight className="policy-agent-chain__arrow" aria-hidden="true" />}
      </div>)}
    </div>
    <p className="policy-visual__note">以 A → B → C 的点位为例。存在点位差时产生差额提成，同点位之间差额为 0；详细提成见下方示例表。</p>
  </figure>;

  if (section.id === "commission-plan" && table?.type === "table") return <figure className="policy-visual" aria-label="人数条件与佣金档位">
    <figcaption><ChartNoAxesCombined aria-hidden="true" /><span>人数条件与佣金档位<small>制度比例</small></span></figcaption>
    <ol className="policy-tier-chart">{table.rows.map(row => <li key={row[0]}>
      <div><span>{row[0]}</span><strong>{row[1]}</strong></div>
      <span className="policy-tier-chart__track" aria-hidden="true"><span style={{ width: row[1] }} /></span>
    </li>)}</ol>
    <p className="policy-visual__note">比例须满足对应人数条件；最终佣金还需按下文公式扣减相关费用。</p>
  </figure>;

  if (section.id === "invitation-rewards") {
    const periods = section.blocks.find(block => block.type === "list" && block.ordered);
    return <figure className="policy-visual" aria-label="人头费奖励统计与发放周期">
      <figcaption><CalendarDays aria-hidden="true" /><span>每月三个周期<small>制度时间安排</small></span></figcaption>
      <ol className="policy-calendar">{periods?.type === "list" && periods.items.map((item, index) => {
        const [label, detail] = item.split("：");
        const [counting, payment] = detail.split("统计，");
        return <li key={label}><span className="policy-calendar__month" aria-hidden="true">{index + 1}</span><div><strong>{label}</strong><div className="policy-calendar__dates"><span><small>统计</small>{counting}</span><ArrowRight aria-hidden="true" /><span><small>发放</small>{payment.replace("发放；", "")}</span></div></div></li>;
      })}</ol>
      <p className="policy-visual__note">有效会员须在对应周期新注册，且充值与有效投注同时达标；具体标准与排除项见下文。</p>
    </figure>;
  }

  if (section.id === "weekly-rewards") return <figure className="policy-visual" aria-label="周流水双条件达标示意">
    <figcaption><Trophy aria-hidden="true" /><span>两项条件，同时达标<small>制度示例</small></span></figcaption>
    <div className="policy-weekly-conditions">
      <div><ChartNoAxesCombined aria-hidden="true" /><span>周流水</span><strong>100w</strong></div>
      <span className="policy-visual__plus" aria-hidden="true">＋</span>
      <div><Users aria-hidden="true" /><span>有效新增</span><strong>5 个</strong></div>
    </div>
    <ArrowDown className="policy-visual__down" aria-hidden="true" />
    <div className="policy-weekly-result"><CircleCheck aria-hidden="true" /><span>共同达标档位对应奖励<strong>388 元</strong></span></div>
    <p className="policy-visual__note">本例流水达到 100w，但有效新增为 5 人，因此对应 388 元奖励。两项条件按各自达到的档位取较低档。</p>
  </figure>;

  if (section.id === "partner-plan") {
    const tables = section.blocks.filter(block => block.type === "table");
    return <figure className="policy-visual" aria-label="两类合营伙伴推荐奖励">
      <figcaption><Handshake aria-hidden="true" /><span>推荐的是合营代理<small>两类奖励分别查看</small></span></figcaption>
      <div className="policy-partner-paths">{tables.map((item, index) => <div className="policy-partner-path" key={item.caption}>
        <span className="policy-partner-path__icon" aria-hidden="true">{index === 0 ? <Network /> : <ChartNoAxesCombined />}</span>
        <div><strong>{item.caption}</strong><p>{item.columns.slice(0, -1).join(" ＋ ")}</p><span><ArrowRight aria-hidden="true" />{item.columns.at(-1)}</span></div>
      </div>)}</div>
      <p className="policy-visual__note">被推荐人须为直属合营下级，普通邀请好友下级不参与；两项计划的资格和周期分别以下文为准。</p>
    </figure>;
  }
  return null;
}
